export const TAX_RATE = 0.14;

/** True when an item sells at 0% HST. Mirrors `ItemFlags::is_tax_exempt`
 *  on the server — the flag is the same one that makes the item sync to
 *  Square with no tax attached. Absent or false means taxed normally. */
export const isTaxExempt = (item?: IItem): boolean => !!item?.flags?.taxExempt;

/** Sum of the lines that HST actually applies to, in cents. Exempt lines
 *  still count toward the subtotal the customer sees — they just don't
 *  contribute to the taxable base. */
export const taxableSubtotal = (items: ICartItem[]): number =>
  items.reduce(
    (sum, i) => (isTaxExempt(i.item) ? sum : sum + i.count * (i.item.price ?? 0)),
    0,
  );

/** Tax on a basket where only part of the subtotal is taxable, in cents.
 *
 *  Mirrors `tax::tax_cents` in backroom-server, which is what the
 *  customer is actually charged — if these two disagree, the storefront
 *  quotes a total the checkout won't honour.
 *
 *  `discount` (a code plus any points redemption) applies to the order
 *  as a whole, so it is allocated pro rata to the taxable share of the
 *  subtotal rather than wholly against the taxable or the exempt side.
 *  Shipping is taxable in NS: callers fold it into both `taxable` and
 *  `subtotal`. */
export const computeTax = (
  taxable: number,
  subtotal: number,
  discount: number,
): number => {
  if (taxable <= 0) return 0;
  const d = Math.max(0, discount);
  const afterDiscount =
    subtotal <= 0 ? taxable : taxable - d * (taxable / subtotal);
  if (afterDiscount <= 0) return 0;
  return Math.round(afterDiscount * TAX_RATE);
};
